import React, { useState, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  CircularProgress,
  Card,
  CardContent,
  CardMedia,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Alert,
  TextField,
  IconButton,
} from '@mui/material';
import UploadIcon from '@mui/icons-material/CloudUpload';
import BugIcon from '@mui/icons-material/BugReport';
import CheckIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import HealingIcon from '@mui/icons-material/Healing';
import ShieldIcon from '@mui/icons-material/Shield';
import CameraIcon from '@mui/icons-material/CameraAlt';
import DeleteIcon from '@mui/icons-material/Delete';
import { pestDetectionService, DetectionResult } from '../../services/pestDetectionService';
import { isDemoModeEnabled } from '../../services/featureFlags';

const PestDetection: React.FC = () => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<DetectionResult | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    setError(null);
    setResult(null);
    
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0];
      
      // Validate file type
      if (!file.type.match('image.*')) {
        setError('Please select an image file (JPEG, PNG)');
        return;
      }
      
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size should be less than 5MB');
        return;
      }
      
      setSelectedFile(file);
      
      // Create preview URL
      const reader = new FileReader();
      reader.onload = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleCameraCapture = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemoveImage = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setResult(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async () => {
    if (!selectedFile) return;
    
    setLoading(true);
    setError(null);
    
    try {
      if (selectedFile instanceof File) {
        // Use real AI API for detection
        const result = await pestDetectionService.detectDisease(selectedFile);
        setResult(result);
      }
      if (isDemoModeEnabled()) {
        setTimeout(() => {
          setLoading(false);
        }, 1000);
      } else {
        setLoading(false);
      }
    } catch (error: any) {
      setError(error?.message || 'Failed to analyze image. Please try again.');
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          <BugIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          AI Pest & Disease Detection
        </Typography>
        <Typography variant="body1" paragraph>
          Upload a photo of your crop to identify pests or diseases and get treatment recommendations.
        </Typography>
        
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                p: 3,
                textAlign: 'center',
                bgcolor: '#f9f9f9',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              {previewUrl ? (
                <Box sx={{ position: 'relative', width: '100%', maxHeight: 300 }}>
                  <img
                    src={previewUrl}
                    alt="Preview"
                    style={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain' }}
                  />
                  <IconButton
                    sx={{ position: 'absolute', top: 0, right: 0 }}
                    onClick={handleRemoveImage}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Box>
              ) : (
                <>
                  <UploadIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Drag & Drop or Click to Upload
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Supported formats: JPEG, PNG (Max size: 5MB)
                  </Typography>
                </>
              )}
              
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                style={{ display: 'none' }}
                ref={fileInputRef}
              />
              
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<UploadIcon />}
                  onClick={() => fileInputRef.current?.click()}
                >
                  Browse Files
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<CameraIcon />}
                  onClick={handleCameraCapture}
                >
                  Take Photo
                </Button>
              </Box>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              {loading ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <CircularProgress size={60} />
                  <Typography variant="h6" sx={{ mt: 2 }}>
                    Analyzing your image...
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Our AI is identifying potential issues
                  </Typography>
                </Box>
              ) : result ? (
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                      <BugIcon sx={{ mr: 1, color: 'error.main' }} />
                      {result.disease}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Typography variant="body2" color="textSecondary" sx={{ mr: 1 }}>
                        Confidence:
                      </Typography>
                      <Box sx={{ position: 'relative', display: 'inline-flex', alignItems: 'center' }}>
                        <CircularProgress
                          variant="determinate"
                          value={result.confidence * 100}
                          size={24}
                          thickness={5}
                          sx={{ color: result.confidence > 0.7 ? 'success.main' : 'warning.main' }}
                        />
                        <Typography
                          variant="caption"
                          sx={{ position: 'absolute', left: '100%', ml: 1 }}
                        >
                          {Math.round(result.confidence * 100)}%
                        </Typography>
                      </Box>
                    </Box>
                    
                    <Typography variant="body1" paragraph>
                      <InfoIcon sx={{ fontSize: 'small', mr: 0.5, verticalAlign: 'middle', color: 'info.main' }} />
                      {result.description}
                    </Typography>
                    
                    <Divider sx={{ my: 2 }} />
                    
                    <Typography variant="h6" gutterBottom>
                      <HealingIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'success.main' }} />
                      Treatment Recommendations
                    </Typography>
                    
                    <List dense>
                      {result.treatment.map((item, index) => (
                        <ListItem key={`treatment-${index}`}>
                          <ListItemIcon>
                            <CheckIcon color="success" />
                          </ListItemIcon>
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                    </List>
                    
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      <ShieldIcon sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                      Prevention Measures
                    </Typography>
                    
                    <List dense>
                      {result.preventionMeasures.map((item, index) => (
                        <ListItem key={`prevention-${index}`}>
                          <ListItemIcon>
                            <CheckIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText primary={item} />
                        </ListItem>
                      ))}
                    </List>
                  </CardContent>
                </Card>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                  <Typography variant="h6" gutterBottom>
                    Upload an image to get started
                  </Typography>
                  <Typography variant="body2" color="textSecondary" align="center">
                    Our AI will analyze your crop image and provide disease identification
                    and treatment recommendations.
                  </Typography>
                  
                  {selectedFile && (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<BugIcon />}
                      onClick={handleSubmit}
                      sx={{ mt: 3 }}
                    >
                      Analyze Image
                    </Button>
                  )}
                </Box>
              )}
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Common Crop Diseases
        </Typography>
        <Typography variant="body2" paragraph>
          Learn about common crop diseases and how to identify them.
        </Typography>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardMedia
                component="img"
                height="140"
                image="https://source.unsplash.com/random/300x200/?plant-disease"
                alt="Late Blight"
              />
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Late Blight
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  A destructive disease affecting potatoes and tomatoes, causing dark lesions on leaves and stems.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card>
              <CardMedia
                component="img"
                height="140"
                image="https://source.unsplash.com/random/300x200/?plant-fungus"
                alt="Powdery Mildew"
              />
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Powdery Mildew
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  A fungal disease appearing as white powdery spots on leaves, stems, and sometimes fruit.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card>
              <CardMedia
                component="img"
                height="140"
                image="https://source.unsplash.com/random/300x200/?aphids"
                alt="Aphid Infestation"
              />
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Aphid Infestation
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Small sap-sucking insects that cause stunted growth and leaf curl, often found on new growth.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default PestDetection;
